import { useMemo, useSyncExternalStore } from 'react';
import { clientMetadataService } from '@/services/clientMetadataService';
import type { ClientMetadata } from '@/types';
import { flagReader, type FlagReader } from '@/utils/clientFlags';

export type ClientFlagsStatus = 'loading' | 'ready' | 'error';

export interface ClientFlags extends FlagReader {
  /**
   * `loading` until the first read is back, `error` when the newest read
   * failed. Until the tags are `ready` every client reads as active and not
   * blocked, so a list is never empty while it waits.
   */
  status: ClientFlagsStatus;
  /** Why the newest read failed. Null otherwise. */
  error: string | null;
  /** Every client's row, by client name: the rows the tags are read from. */
  metadata: ReadonlyMap<string, ClientMetadata>;
  /** Read the table again now. Resolves when the new tags are in. */
  reload: () => Promise<void>;
}

interface Snapshot {
  status: ClientFlagsStatus;
  error: string | null;
  metadata: ReadonlyMap<string, ClientMetadata>;
}

/** How often an open page reads the tags again. A tag set by someone else shows within this time. */
const REFRESH_MS = 5 * 60 * 1000;

const NO_ROWS: ReadonlyMap<string, ClientMetadata> = new Map();
let snapshot: Snapshot = { status: 'loading', error: null, metadata: NO_ROWS };
const subscribers = new Set<() => void>();
let loading: Promise<void> | null = null;
let readAgain = false;
let readTable = false;
let stopListening: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function publish(next: Snapshot): void {
  if (next.status === snapshot.status && next.error === snapshot.error && next.metadata === snapshot.metadata) return;
  snapshot = next;
  for (const notify of [...subscribers]) notify();
}

/**
 * Read the rows through the metadata service, which keeps them for a few
 * minutes. `fromTable` skips what the service holds and reads the table. A
 * change that lands while a read is in flight starts one more read after it,
 * so the tags on screen never settle on rows from before the change.
 */
function load(fromTable = false): Promise<void> {
  if (fromTable) readTable = true;
  if (loading) {
    readAgain = true;
    return loading;
  }
  loading = (async () => {
    do {
      readAgain = false;
      const fresh = readTable;
      readTable = false;
      try {
        const metadata = await clientMetadataService.loadAllMetadata({ fresh });
        publish({ status: 'ready', error: null, metadata });
      } catch (e) {
        // The rows already on screen stay: a failed refresh must not take a tag off a client.
        publish({
          status: 'error',
          error: e instanceof Error ? e.message : 'The client tags could not be read.',
          metadata: snapshot.metadata,
        });
      }
    } while (readAgain);
  })().finally(() => {
    loading = null;
  });
  return loading;
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  if (subscribers.size === 1) {
    // Any write through the service (here or on another page of this tab) refreshes the tags.
    stopListening = clientMetadataService.subscribe(() => void load());
    timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') void load(true);
    }, REFRESH_MS);
  }
  // Costs nothing while the service still holds the rows. Reads the table when they are old.
  void load();
  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0) {
      stopListening?.();
      stopListening = null;
      if (timer) clearInterval(timer);
      timer = null;
    }
  };
}

const getSnapshot = () => snapshot;

function reload(): Promise<void> {
  // Clearing the service's rows tells this store, which starts the read.
  clientMetadataService.clearCache();
  return loading ?? load();
}

/**
 * The two tags a person can put on a client: "do not deploy" and "inactive".
 * Every page reads them through this hook, so the table is read once for the
 * whole tab and each page shows the same answer.
 *
 * It only reads. Tags are set and lifted through `clientMetadataService`,
 * and this hook picks the change up by itself.
 */
export function useClientFlags(): ClientFlags {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(
    () => ({
      status: current.status,
      error: current.error,
      metadata: current.metadata,
      ...flagReader(current.metadata),
      reload,
    }),
    [current]
  );
}

/** For tests: forget everything, as a fresh tab would. */
export function resetClientFlagsForTests(): void {
  snapshot = { status: 'loading', error: null, metadata: NO_ROWS };
  loading = null;
  readAgain = false;
  readTable = false;
}

export default useClientFlags;
